import { AnalyticsEvent, AnalyticsEventName, AnalyticsProperties, AuthStudioTemplateType, MfaFactor, TrackingMeta } from "./types";
import { analyticsClient } from "./client";
import { buildAnalyticsEvent } from "./payload";

export interface MutationTrackingState {
  pendingTrackingMeta?: TrackingMeta;
  pendingRequestId?: string;
  lastTrackingMeta?: TrackingMeta;
  lastRequestId?: string;
  lastOutcome?: "success" | "error";
}

type ActionWithMeta = {
  payload?: unknown;
  meta?: {
    trackingMeta?: TrackingMeta;
  };
};

type ErrorCarrier = {
  error?: string;
  errorMessage?: string | null;
  message?: string;
  Message?: string;
  description?: string;
  Description?: string;
  ErrorMessage?: string;
  Reason?: string;
};

const createRequestId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const createTrackingMeta = (meta: Omit<TrackingMeta, "requestId"> & { requestId?: string }) => ({
  ...meta,
  requestId: meta.requestId || createRequestId()
});

export const getTrackingMeta = (action?: ActionWithMeta): TrackingMeta | undefined => action?.meta?.trackingMeta;

export const getRequestTrackingState = (action?: ActionWithMeta): MutationTrackingState => {
  const trackingMeta = getTrackingMeta(action);

  if (!trackingMeta) {
    return {};
  }

  return {
    pendingTrackingMeta: trackingMeta,
    pendingRequestId: trackingMeta.requestId
  };
};

export const getOutcomeTrackingState = (state?: MutationTrackingState, outcome?: "success" | "error"): MutationTrackingState => {
  if (!state?.pendingRequestId || !outcome) {
    return {};
  }

  return {
    pendingTrackingMeta: undefined,
    pendingRequestId: undefined,
    lastTrackingMeta: state.pendingTrackingMeta,
    lastRequestId: state.pendingRequestId,
    lastOutcome: outcome
  };
};

export const mapTrackedOutcome = (
  previousState: MutationTrackingState | undefined,
  currentState: (MutationTrackingState & ErrorCarrier) | undefined,
  fallback?: {
    successEventName?: AnalyticsEventName;
    errorEventName?: AnalyticsEventName;
    properties?: AnalyticsProperties;
  }
): AnalyticsEvent[] => {
  const currentRequestId = currentState?.lastRequestId;

  if (!currentRequestId || previousState?.lastRequestId === currentRequestId || !currentState?.lastOutcome) {
    return [];
  }

  const trackingMeta = currentState.lastTrackingMeta;
  const isError = currentState.lastOutcome === "error";
  const eventName = isError ? trackingMeta?.errorEventName || fallback?.errorEventName : trackingMeta?.eventName || fallback?.successEventName;

  if (!eventName) {
    return [];
  }

  const properties: AnalyticsProperties = {
    ...(fallback?.properties || {}),
    ...(trackingMeta?.properties || {})
  };

  if (trackingMeta?.operation) {
    properties.operation = trackingMeta.operation;
  }
  if (trackingMeta?.entityType) {
    properties.entity_type = trackingMeta.entityType;
  }
  if (trackingMeta?.providerType) {
    properties.provider_type = trackingMeta.providerType;
  }
  if (trackingMeta?.factor) {
    properties.factor = trackingMeta.factor;
  }
  if (trackingMeta?.templateType) {
    properties.template_type = trackingMeta.templateType;
  }
  if (typeof trackingMeta?.targetEnabled === "boolean") {
    properties.target_enabled = trackingMeta.targetEnabled;
  }

  if (isError) {
    const errorMessage =
      currentState.error ||
      currentState.errorMessage ||
      currentState.message ||
      currentState.Message ||
      currentState.description ||
      currentState.Description ||
      currentState.ErrorMessage ||
      currentState.Reason;

    if (errorMessage) {
      properties.error_message = errorMessage;
    }
  }

  return [
    {
      name: eventName,
      properties,
      forError: isError
    }
  ];
};

export type TrackingMetaArgs = {
  eventName?: AnalyticsEventName;
  errorEventName?: AnalyticsEventName;
  properties?: AnalyticsProperties;
  operation?: string;
  entityType?: string;
  providerType?: string;
  targetEnabled?: boolean;
  factor?: MfaFactor;
  templateType?: AuthStudioTemplateType;
};

export const withTrackingMeta = <T extends { type: string; payload: unknown }>(action: T, trackingMeta?: TrackingMetaArgs) => {
  if (!trackingMeta) {
    return action;
  }

  return {
    ...action,
    meta: {
      trackingMeta: createTrackingMeta(trackingMeta)
    }
  };
};

export const trackEvent = (event: AnalyticsEvent) => {
  analyticsClient.track(buildAnalyticsEvent(event));
};
